import { TextField, type TextFieldProps } from "@mui/material";
import { forwardRef } from "react";
import { IMaskInput } from "react-imask";

/**
 * Máscara de teléfono: (000) 000-0000
 */
const PHONE_MASK = '(000) 000-0000';
const PHONE_PLACEHOLDER = '(809) 555-1234';

interface PhoneInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
    /** Valor del teléfono */
    value?: string;
    /** Callback cuando cambia el valor */
    onChange?: (value: string) => void;
    /** Callback cuando pierde el foco */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * Componente de input para teléfonos con máscara (000) 000-0000
 * 
 * @example
 * ```tsx
 * <PhoneInput
 *   value={phone}
 *   onChange={(value) => setPhone(value)}
 *   label="Teléfono"
 *   required
 * />
 * ```
 */
export const PhoneInput = forwardRef<HTMLDivElement, PhoneInputProps>(({
    value = '',
    onChange,
    onBlur,
    label = 'Teléfono',
    placeholder,
    sx,
    ...textFieldProps
}, ref) => {
    const handleAccept = (value: string) => {
        if (onChange) {
            onChange(value);
        }
    };

    return (
        <TextField
            {...textFieldProps}
            ref={ref}
            fullWidth
            label={label}
            placeholder={placeholder || PHONE_PLACEHOLDER}
            value={value}
            sx={{ 
                backgroundColor: '#FFFFFF',
                ...sx 
            }}
            InputLabelProps={{
                shrink: true,
            }}
            InputProps={{
                inputComponent: IMaskInput as any,
                inputProps: {
                    mask: PHONE_MASK,
                    lazy: true,  // Solo muestra la máscara cuando se escribe
                    placeholderChar: '_',
                    onAccept: handleAccept,
                    onBlur: onBlur,
                },
            }}
        />
    );
});

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
